import { useMemo, useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
  Modal,
} from "react-native";
import {
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Clock,
  MapPin,
  Search,
  X,
  LockKeyhole,
  Droplet,
  Microscope,
  BarChart3,
  Dna,
  FlaskConical,
} from "lucide-react-native";
import Svg, { Circle, Ellipse, Line, Path } from "react-native-svg";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { calculateBasicInfoCompletion } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { useFertilityIntent } from "@src/packages/fertility-intent";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import {
  getBlocksByKind,
  type ContentBlock,
  type TestBody,
} from "@src/packages/content";
import { getTestingRecommendations } from "./testingRecommendations";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { IconButton, MoonSearchInput, SectionTitle } from "@louise/moon-ui";
import {
  colors,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import StatusBadge from "../../components/StatusBadge";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { getTestRouteTarget } from "./testRouteIds";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";

/* ─── Section definitions ──────────────────────────────────────────── */

type SectionDef = {
  key: string;
  titleKey: string;
  descriptionKey: string;
  icon: typeof Droplet;
};

type SectionGenderRelevance = "AFAB" | "AMAB" | "both";

const SECTIONS: (SectionDef & { gender: SectionGenderRelevance })[] = [
  {
    key: "ovarian-reserve",
    titleKey: "access.testing.sections.ovarian_reserve_title",
    descriptionKey: "access.testing.sections.ovarian_reserve_desc",
    icon: Droplet,
    gender: "AFAB",
  },
  {
    key: "semen-analysis",
    titleKey: "access.testing.sections.semen_analysis_title",
    descriptionKey: "access.testing.sections.semen_analysis_desc",
    icon: Microscope,
    gender: "AMAB",
  },
  {
    key: "cycle-monitoring",
    titleKey: "access.testing.sections.cycle_monitoring_title",
    descriptionKey: "access.testing.sections.cycle_monitoring_desc",
    icon: BarChart3,
    gender: "AFAB",
  },
  {
    key: "genetic-screening",
    titleKey: "access.testing.sections.genetic_screening_title",
    descriptionKey: "access.testing.sections.genetic_screening_desc",
    icon: Dna,
    gender: "both",
  },
  {
    key: "implantation",
    titleKey: "access.testing.sections.implantation_title",
    descriptionKey: "access.testing.sections.implantation_desc",
    icon: FlaskConical,
    gender: "AFAB",
  },
];

/**
 * Order sections by relevance to the profile, but always show all of them.
 * Everyone can order any test (partners often order semen analysis), so nothing
 * is hidden. We just lead with what fits this person: an AFAB profile sees the
 * her-focused sections first and semen analysis lower down; an AMAB profile sees
 * semen analysis first. Unknown gender keeps the default order.
 */
function prioritizeSectionsForGender(gender: string) {
  if (gender !== "AFAB" && gender !== "AMAB") return SECTIONS;
  const relevant = SECTIONS.filter(
    (s) => s.gender === gender || s.gender === "both",
  );
  const rest = SECTIONS.filter(
    (s) => s.gender !== gender && s.gender !== "both",
  );
  return [...relevant, ...rest];
}

/* ─── Guide cards — user-friendly questions ──────────────────────── */

type GuideCard = {
  key: string;
  labelKey: string;
  subtitleKey: string;
  overviewKey: string;
  illustration: TestingGuideIllustrationKind;
  sections: string[];
  detailSections: GuideDetailSection[];
  relatedItemIds: string[];
};

type GuideDetailSection = {
  titleKey: string;
  bodyKey: string;
};

type TestingGuideIllustrationKind =
  | "fertility"
  | "genetic"
  | "ovulation"
  | "implantation"
  | "semen"
  | "homeKit"
  | "baseline";

const GUIDE_CARDS: GuideCard[] = [
  {
    key: "fertility",
    labelKey: "access.testing.guides.fertility.label",
    subtitleKey: "access.testing.guides.fertility.subtitle",
    overviewKey: "access.testing.guides.fertility.overview",
    illustration: "fertility",
    sections: ["ovarian-reserve", "semen-analysis"],
    detailSections: [
      {
        titleKey: "access.testing.guides.fertility.detail_1_title",
        bodyKey: "access.testing.guides.fertility.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.fertility.detail_2_title",
        bodyKey: "access.testing.guides.fertility.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-amh",
      "test-afc",
      "test-hormone-panel",
      "test-comprehensive-biomarker-panel",
      "test-semen-analysis",
      "product-modern-fertility",
      "product-semen-core",
    ],
  },
  {
    key: "amh",
    labelKey: "access.testing.guides.amh.label",
    subtitleKey: "access.testing.guides.amh.subtitle",
    overviewKey: "access.testing.guides.amh.overview",
    illustration: "fertility",
    sections: ["ovarian-reserve"],
    detailSections: [
      {
        titleKey: "access.testing.guides.amh.detail_1_title",
        bodyKey: "access.testing.guides.amh.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.amh.detail_2_title",
        bodyKey: "access.testing.guides.amh.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-amh",
      "test-afc",
      "test-hormone-panel",
      "product-modern-fertility",
      "product-letsgetchecked",
      "product-everlywell",
    ],
  },
  {
    key: "sperm",
    labelKey: "access.testing.guides.sperm.label",
    subtitleKey: "access.testing.guides.sperm.subtitle",
    overviewKey: "access.testing.guides.sperm.overview",
    illustration: "semen",
    sections: ["semen-analysis"],
    detailSections: [
      {
        titleKey: "access.testing.guides.sperm.detail_1_title",
        bodyKey: "access.testing.guides.sperm.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.sperm.detail_2_title",
        bodyKey: "access.testing.guides.sperm.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-semen-analysis",
      "product-semen-core",
      "product-semen-dna-frag",
      "product-semen-complete",
    ],
  },
  {
    key: "ovulation",
    labelKey: "access.testing.guides.ovulation.label",
    subtitleKey: "access.testing.guides.ovulation.subtitle",
    overviewKey: "access.testing.guides.ovulation.overview",
    illustration: "ovulation",
    sections: ["cycle-monitoring"],
    detailSections: [
      {
        titleKey: "access.testing.guides.ovulation.detail_1_title",
        bodyKey: "access.testing.guides.ovulation.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.ovulation.detail_2_title",
        bodyKey: "access.testing.guides.ovulation.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-fertility-tracker",
      "test-progesterone-check",
      "test-general-bloodwork",
      "test-monitoring-ultrasound",
      "product-mira",
    ],
  },
  {
    key: "embryo_testing",
    labelKey: "access.testing.guides.embryo_testing.label",
    subtitleKey: "access.testing.guides.embryo_testing.subtitle",
    overviewKey: "access.testing.guides.embryo_testing.overview",
    illustration: "genetic",
    sections: ["genetic-screening"],
    detailSections: [
      {
        titleKey: "access.testing.guides.embryo_testing.detail_1_title",
        bodyKey: "access.testing.guides.embryo_testing.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.embryo_testing.detail_2_title",
        bodyKey: "access.testing.guides.embryo_testing.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-carrier-screening",
      "test-whole-genome",
      "test-preimplantation-genetic-testing",
      "test-pgt-a",
      "test-pgt-m",
      "test-pgt-sr",
      "test-pgt-p",
      "test-karyotype",
    ],
  },
  {
    key: "uterus",
    labelKey: "access.testing.guides.uterus.label",
    subtitleKey: "access.testing.guides.uterus.subtitle",
    overviewKey: "access.testing.guides.uterus.overview",
    illustration: "implantation",
    sections: ["implantation"],
    detailSections: [
      {
        titleKey: "access.testing.guides.uterus.detail_1_title",
        bodyKey: "access.testing.guides.uterus.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.uterus.detail_2_title",
        bodyKey: "access.testing.guides.uterus.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-saline-sonogram",
      "test-lining-check",
      "test-endometrial-receptivity-testing",
      "test-endometrial-microbiome-testing",
      "test-chronic-endometritis-testing",
      "test-endometrial-add-on-bundle",
      "test-beta-hcg",
    ],
  },
  {
    key: "at_home",
    labelKey: "access.testing.guides.at_home.label",
    subtitleKey: "access.testing.guides.at_home.subtitle",
    overviewKey: "access.testing.guides.at_home.overview",
    illustration: "homeKit",
    sections: ["ovarian-reserve", "cycle-monitoring", "semen-analysis"],
    detailSections: [
      {
        titleKey: "access.testing.guides.at_home.detail_1_title",
        bodyKey: "access.testing.guides.at_home.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.at_home.detail_2_title",
        bodyKey: "access.testing.guides.at_home.detail_2_body",
      },
    ],
    relatedItemIds: [
      "product-modern-fertility",
      "product-letsgetchecked",
      "product-letsgetchecked-hormone",
      "product-everlywell",
      "product-mira",
      "product-semen-core",
      "product-semen-complete",
    ],
  },
  {
    key: "baseline",
    labelKey: "access.testing.guides.baseline.label",
    subtitleKey: "access.testing.guides.baseline.subtitle",
    overviewKey: "access.testing.guides.baseline.overview",
    illustration: "baseline",
    sections: ["cycle-monitoring", "implantation", "genetic-screening"],
    detailSections: [
      {
        titleKey: "access.testing.guides.baseline.detail_1_title",
        bodyKey: "access.testing.guides.baseline.detail_1_body",
      },
      {
        titleKey: "access.testing.guides.baseline.detail_2_title",
        bodyKey: "access.testing.guides.baseline.detail_2_body",
      },
    ],
    relatedItemIds: [
      "test-baseline-ultrasound",
      "test-general-bloodwork",
      "test-progesterone-check",
      "test-infectious-disease",
      "test-cbc",
      "test-thyroid-panel",
      "test-saline-sonogram",
      "test-carrier-screening",
    ],
  },
];

function TestingGuideIllustration({
  kind,
}: {
  kind: TestingGuideIllustrationKind;
}) {
  const ink = "#4A302C";
  const softInk = "rgba(74, 48, 44, 0.22)";
  const clay = "#9B5E50";
  const mauve = "#7C5961";
  const apricot = "#D4946A";
  const fill = "rgba(255,244,236,0.66)";
  const clayWash = "rgba(155,94,80,0.1)";
  const mauveWash = "rgba(124,89,97,0.1)";
  const apricotWash = "rgba(212,148,106,0.14)";

  return (
    <View
      style={{
        width: 84,
        height: 62,
        alignItems: "center",
        justifyContent: "center",
        marginTop: -4,
        marginLeft: -8,
      }}
    >
      {kind === "fertility" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Ellipse cx={42} cy={34} rx={34} ry={20} fill={clayWash} />
          {[19, 34, 49].map((cx, index) => (
            <Circle
              key={cx}
              cx={cx}
              cy={[38, 24, 36][index]}
              r={[9, 11, 10][index]}
              fill={fill}
              stroke={clay}
              strokeWidth={1.7}
            />
          ))}
          <Circle
            cx={62}
            cy={28}
            r={14}
            fill={fill}
            stroke={mauve}
            strokeWidth={2}
          />
          <Circle cx={62} cy={28} r={4.5} stroke={ink} strokeWidth={1.3} />
          <Path
            d="M13 51H69"
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </Svg>
      ) : null}

      {kind === "genetic" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Ellipse cx={43} cy={33} rx={32} ry={21} fill={mauveWash} />
          <Circle
            cx={35}
            cy={34}
            r={17}
            fill={fill}
            stroke={mauve}
            strokeWidth={1.8}
          />
          <Circle
            cx={52}
            cy={28}
            r={12}
            fill={fill}
            stroke={clay}
            strokeWidth={1.6}
          />
          <Path
            d="M28 25C36 30 34 39 43 44"
            stroke={ink}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path
            d="M43 24C35 30 37 39 28 44"
            stroke={ink}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          {[30, 37, 42].map((y, index) => (
            <Line
              key={y}
              x1={[31, 33, 36][index]}
              y1={y}
              x2={[40, 38, 31][index]}
              y2={y}
              stroke={apricot}
              strokeWidth={1.3}
              strokeLinecap="round"
            />
          ))}
          <Path
            d="M56 18L59 21L65 14"
            stroke={clay}
            strokeWidth={1.7}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      ) : null}

      {kind === "ovulation" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Path
            d="M16 47H70"
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path
            d="M18 43C26 39 29 28 37 27C47 25 48 40 58 31C63 27 65 20 70 16"
            stroke={mauve}
            strokeWidth={2.4}
            strokeLinecap="round"
          />
          {[20, 37, 57, 70].map((cx, index) => (
            <Circle
              key={cx}
              cx={cx}
              cy={[42, 27, 32, 16][index]}
              r={3.5}
              fill="#FFF8F0"
              stroke={ink}
              strokeWidth={1.2}
            />
          ))}
          <Circle
            cx={27}
            cy={18}
            r={9}
            fill={fill}
            stroke={clay}
            strokeWidth={1.5}
          />
          <Circle cx={27} cy={18} r={3} stroke={ink} strokeWidth={1.1} />
        </Svg>
      ) : null}

      {kind === "implantation" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Ellipse cx={37} cy={37} rx={24} ry={15} fill={apricotWash} />
          <Circle
            cx={32}
            cy={34}
            r={11}
            fill={fill}
            stroke={apricot}
            strokeWidth={1.8}
          />
          <Circle
            cx={47}
            cy={39}
            r={8}
            fill={fill}
            stroke={clay}
            strokeWidth={1.5}
          />
          <Path
            d="M69 14L47 31"
            stroke={ink}
            strokeWidth={1.8}
            strokeLinecap="round"
          />
          <Path
            d="M66 11L74 19"
            stroke={ink}
            strokeWidth={1.8}
            strokeLinecap="round"
          />
          <Circle cx={47} cy={31} r={3} fill="rgba(230,177,144,0.7)" />
          <Path
            d="M19 49C29 54 48 54 60 47"
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </Svg>
      ) : null}

      {kind === "semen" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Ellipse cx={44} cy={33} rx={33} ry={20} fill={mauveWash} />
          <Circle
            cx={27}
            cy={34}
            r={9}
            fill={fill}
            stroke={mauve}
            strokeWidth={1.8}
          />
          <Path
            d="M36 34C47 32 50 22 63 22C69 22 72 25 75 29"
            stroke={ink}
            strokeWidth={1.7}
            strokeLinecap="round"
          />
          <Circle
            cx={50}
            cy={43}
            r={7}
            fill={fill}
            stroke={clay}
            strokeWidth={1.5}
          />
          <Path
            d="M57 43C64 43 67 48 72 51"
            stroke={clay}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Circle cx={27} cy={34} r={3} stroke={ink} strokeWidth={1.1} />
          <Path
            d="M15 51H69"
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </Svg>
      ) : null}

      {kind === "homeKit" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Ellipse cx={43} cy={38} rx={31} ry={17} fill={clayWash} />
          <Path
            d="M25 25H59L64 33V48H20V33L25 25Z"
            fill={fill}
            stroke={clay}
            strokeWidth={1.7}
            strokeLinejoin="round"
          />
          <Path
            d="M20 33H64"
            stroke={softInk}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <Path
            d="M35 25L42 34L49 25"
            stroke={mauve}
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d="M31 17H42L39 25H34L31 17Z"
            fill="rgba(255,248,240,0.9)"
            stroke={ink}
            strokeWidth={1.4}
            strokeLinejoin="round"
          />
          <Circle
            cx={53}
            cy={42}
            r={5.5}
            fill="rgba(212,148,106,0.22)"
            stroke={apricot}
            strokeWidth={1.3}
          />
        </Svg>
      ) : null}

      {kind === "baseline" ? (
        <Svg width={84} height={62} viewBox="0 0 84 62" fill="none">
          <Ellipse cx={42} cy={34} rx={30} ry={20} fill={apricotWash} />
          <Path
            d="M27 15H56C59 15 61 17 61 20V48C61 51 59 53 56 53H27C24 53 22 51 22 48V20C22 17 24 15 27 15Z"
            fill={fill}
            stroke={clay}
            strokeWidth={1.7}
          />
          <Path
            d="M33 14H51L49 21H35L33 14Z"
            fill="rgba(255,248,240,0.92)"
            stroke={ink}
            strokeWidth={1.3}
            strokeLinejoin="round"
          />
          {[29, 37, 45].map((cy) => (
            <Circle
              key={cy}
              cx={31}
              cy={cy}
              r={2.4}
              fill="rgba(212,148,106,0.24)"
              stroke={apricot}
              strokeWidth={1.1}
            />
          ))}
          {[29, 37, 45].map((y, index) => (
            <Line
              key={y}
              x1={38}
              y1={y}
              x2={[53, 48, 55][index]}
              y2={y}
              stroke={index === 1 ? mauve : softInk}
              strokeWidth={1.4}
              strokeLinecap="round"
            />
          ))}
        </Svg>
      ) : null}
    </View>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

type TestLocation = "at_home" | "clinic" | "lab";

function getTestLocationKey(body: TestBody): TestLocation {
  if (body.atHome) return "at_home";
  if (body.clinicRequired) return "clinic";
  return "lab";
}

const testingPlanHeroBg =
  require("../../../assets/images/care-hero-glow-gradient.png") as number;

function textMatchesQuery(
  values: (string | string[] | undefined)[],
  q: string,
) {
  return values.some((value) => {
    if (!value) return false;
    const text = Array.isArray(value) ? value.join(" ") : value;
    return text.toLowerCase().includes(q);
  });
}

function testMatchesSearch(test: ContentBlock<"test">, q: string) {
  const body = test.body;
  return textMatchesQuery(
    [
      test.title,
      test.subtitle,
      test.tags,
      body.testName,
      body.whatItMeasures,
      body.howItWorks,
      body.turnaroundTime,
      body.cycleDay,
    ],
    q,
  );
}

function productMatchesSearch(product: ContentBlock<"product">, q: string) {
  const body = product.body;
  return textMatchesQuery(
    [product.title, product.tags, body.name, body.description, body.chips],
    q,
  );
}

type TestingItem = ContentBlock<"test"> | ContentBlock<"product">;

function getTestingItemSection(item: TestingItem) {
  return item.body.section;
}

function getTestingItemPrice(item: TestingItem) {
  if (item.kind === "product") {
    return `$${item.body.price.toLocaleString()}`;
  }
  if (!item.body.costRange) return null;
  return `$${item.body.costRange.low.toLocaleString()}–$${item.body.costRange.high.toLocaleString()}`;
}

/** Compact cost format for tight rows: 3k instead of 3,000. No leading $ - caller adds icon. */
function formatCostShort(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    const formatted =
      k % 1 === 0 ? k.toString() : k.toFixed(1).replace(/\.0$/, "");
    return `${formatted}k`;
  }
  return `${n}`;
}

function getTestingItemSummary(item: TestingItem) {
  if (item.kind === "product") {
    return item.body.description;
  }
  return item.subtitle ?? item.body.whatItMeasures;
}

function getGuideRelatedItems(
  card: GuideCard,
  tests: ContentBlock<"test">[],
  products: ContentBlock<"product">[],
  visibleSections: SectionDef[],
) {
  const visibleSectionKeys = new Set(
    visibleSections.map((section) => section.key),
  );
  const allItems: TestingItem[] = [...tests, ...products];

  return card.relatedItemIds
    .map((id) => allItems.find((item) => String(item.id) === id))
    .filter((item): item is TestingItem => {
      if (!item) return false;
      const section = getTestingItemSection(item);
      return !section || visibleSectionKeys.has(section);
    });
}

type RecommendedItem = {
  testId: string;
  title: string;
  reason: string;
};

function TestingRecommendationCards({
  items,
  onPress,
}: {
  items: RecommendedItem[];
  onPress: (testId: string) => void;
}) {
  const capturePress = usePressCapture("TestingRecommendationCards");
  if (items.length === 0) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        gap: 10,
        paddingRight: 4,
      }}
      style={{
        marginTop: 16,
        marginHorizontal: -4,
      }}
    >
      {items.map((item) => {
        return (
          <Pressable
            key={item.testId}
            onPress={() => {
              capturePress(
                pressLabel(item.title),
                "testing_recommendation_card",
              );
              onPress(item.testId);
            }}
            accessibilityRole="button"
            accessibilityLabel={`${item.title}. ${item.reason}`}
            style={({ pressed }) => ({
              width: 164,
              minHeight: 138,
              borderRadius: 14,
              backgroundColor: pressed
                ? "rgba(255,255,255,0.18)"
                : "rgba(255,255,255,0.1)",
              borderWidth: 1,
              borderColor: "rgba(255,255,255,0.2)",
              padding: 12,
              justifyContent: "space-between",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            })}
          >
            <View style={{ gap: 6, minWidth: 0 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  numberOfLines={2}
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: "800",
                    color: "#FFFFFF",
                    lineHeight: 18,
                  }}
                >
                  {item.title}
                </Text>
                <ChevronRight
                  size={14}
                  color="rgba(255,255,255,0.72)"
                  strokeWidth={1.8}
                />
              </View>

              <Text
                className="font-dm-sans"
                numberOfLines={3}
                style={{
                  fontSize: 11.5,
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: 16,
                }}
              >
                {item.reason}
              </Text>
            </View>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

function TestingPlanHeroCard({
  items = [],
  onItemPress,
}: {
  items?: RecommendedItem[];
  onItemPress?: (testId: string) => void;
}) {
  const t = useTranslations();
  const navigation = useNavigation();
  const hasRecommendations = items.length > 0 && !!onItemPress;
  const recommendationContent =
    hasRecommendations && onItemPress ? (
      <TestingRecommendationCards items={items} onPress={onItemPress} />
    ) : undefined;

  return (
    <Hero
      style={{ marginTop: 16 }}
      backgroundSource={testingPlanHeroBg}
      eyebrow={{
        label: hasRecommendations
          ? t("access.testing.recommendations.eyebrow")
          : t("access.testing.hero.eyebrow"),
        icon: FlaskConical,
      }}
      title={
        hasRecommendations
          ? t("access.testing.recommendations.title")
          : t("access.testing.hero.title")
      }
      summary={
        hasRecommendations
          ? t("access.testing.recommendations.subtitle")
          : t("access.testing.hero.summary")
      }
      primaryAction={
        hasRecommendations
          ? undefined
          : {
              label: t("access.testing.hero.cta_unlock"),
              icon: LockKeyhole,
              onPress: () =>
                navigation.dispatch(
                  CommonActions.navigate({
                    name: "Care",
                    params: { screen: "MedicalHistory" },
                  }),
                ),
            }
      }
      bottomContent={recommendationContent}
    />
  );
}

function TestingGuideItemRow({
  item,
  isLast,
  onPress,
}: {
  item: TestingItem;
  isLast: boolean;
  onPress: (item: TestingItem) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestingGuideItemRow");
  const isProduct = item.kind === "product";
  const price = getTestingItemPrice(item);
  const locationKey: TestLocation = isProduct
    ? "at_home"
    : getTestLocationKey(item.body);
  const status = t(`access.testing.location.${locationKey}`);
  const statusTone =
    locationKey === "at_home"
      ? "green"
      : locationKey === "clinic"
        ? "orange"
        : "neutral";
  const turnaroundTime = item.kind === "test" ? item.body.turnaroundTime : null;
  const productImageUrl = item.kind === "product" ? item.body.imageUrl : null;
  const productName = item.kind === "product" ? item.body.name : null;

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(item.title), "testing_guide_item_row");
        onPress(item);
      }}
      style={{
        paddingVertical: 12,
        gap: 7,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: "rgba(0,0,0,0.06)",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            flexShrink: 1,
          }}
        >
          <StatusBadge label={status} tone={statusTone} icon={MapPin} />
          {turnaroundTime ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 3,
                flexShrink: 1,
              }}
            >
              <Clock
                size={9}
                color={EMBER.colors.textFaint}
                strokeWidth={1.5}
              />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 10,
                  color: EMBER.colors.textFaint,
                  flexShrink: 1,
                }}
              >
                {turnaroundTime}
              </Text>
            </View>
          ) : null}
        </View>
        {price ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
            }}
          >
            <DollarSign size={11} color={colors.greyBrown} strokeWidth={2} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: colors.greyBrown,
              }}
            >
              {price.replace("$", "")}
            </Text>
          </View>
        ) : null}
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        {productImageUrl ? (
          <Image
            source={{ uri: productImageUrl }}
            style={{
              width: 38,
              height: 38,
            }}
            resizeMode="contain"
          />
        ) : isProduct && item.icon ? (
          <Text style={{ fontSize: 24 }}>{item.icon}</Text>
        ) : null}
        <View style={{ flex: 1, gap: 3 }}>
          <Text
            className="font-dm-sans"
            numberOfLines={2}
            style={{
              fontSize: 14.5,
              fontWeight: "700",
              color: EMBER.colors.text,
              lineHeight: 19,
            }}
          >
            {productName ?? item.title}
          </Text>
          <Text
            className="font-dm-sans"
            numberOfLines={2}
            style={{
              fontSize: 12.5,
              color: EMBER.colors.textLight,
              lineHeight: 17,
            }}
          >
            {getTestingItemSummary(item)}
          </Text>
        </View>
        <ChevronRight
          size={16}
          color={EMBER.colors.textFaint}
          strokeWidth={1.6}
        />
      </View>
    </Pressable>
  );
}

function TestingGuideSheet({
  card,
  items,
  onClose,
  onItemPress,
}: {
  card: GuideCard | null;
  items: TestingItem[];
  onClose: () => void;
  onItemPress: (item: TestingItem) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestingGuideSheet");
  if (!card) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={() => {
          // Backdrop dismiss target; it has no visible copy of its own.
          capturePress(pressLabel("dismiss_testing_guide"));
          onClose();
        }}
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          backgroundColor: "rgba(20, 12, 9, 0.38)",
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={
            {
              width: "100%",
              maxWidth: 440,
              maxHeight: "86%",
              borderRadius: 22,
              overflow: "hidden",
              backgroundColor: "rgba(255,255,255,0.9)",
              borderWidth: 1,
              borderColor: "rgba(255,255,255,0.72)",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(26px) saturate(1.25)",
                WebkitBackdropFilter: "blur(26px) saturate(1.25)",
              }),
            } as Record<string, unknown>
          }
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: 20,
              paddingTop: 18,
              paddingBottom: 22,
            }}
          >
            <IconButton
              onPress={onClose}
              hitSlop={10}
              size={32}
              surface="soft"
              backgroundColor="rgba(0,0,0,0.04)"
              pressedBackgroundColor="rgba(0,0,0,0.04)"
              pressedOpacity={1}
              accessibilityLabel="Close"
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                zIndex: 2,
              }}
            >
              <X size={16} color={EMBER.colors.textLight} strokeWidth={1.8} />
            </IconButton>

            <View style={{ alignItems: "center", paddingTop: 4 }}>
              <TestingGuideIllustration kind={card.illustration} />
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 22,
                lineHeight: 27,
                fontWeight: "800",
                color: EMBER.colors.text,
                textAlign: "center",
                marginTop: 4,
                paddingHorizontal: 18,
              }}
            >
              {t(card.labelKey)}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13.5,
                lineHeight: 20,
                color: EMBER.colors.textLight,
                textAlign: "center",
                marginTop: 8,
              }}
            >
              {t(card.overviewKey)}
            </Text>

            <View style={{ marginTop: 24, gap: 18 }}>
              {card.detailSections.map((section) => (
                <View key={section.titleKey}>
                  <SectionTitle icon={Search}>
                    {t(section.titleKey)}
                  </SectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13.5,
                      lineHeight: 21,
                      color: EMBER.colors.textLight,
                      marginTop: 8,
                    }}
                  >
                    {t(section.bodyKey)}
                  </Text>
                </View>
              ))}
            </View>

            <View style={{ marginTop: 24 }}>
              <SectionTitle icon={FlaskConical}>
                {t("access.testing.tests_and_kits")}
              </SectionTitle>
              <View
                style={{
                  ...(GLASS_CARD_STYLE as object),
                  marginTop: 10,
                  paddingHorizontal: 14,
                  paddingVertical: 3,
                }}
              >
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <TestingGuideItemRow
                      key={String(item.id)}
                      item={item}
                      isLast={index === items.length - 1}
                      onPress={onItemPress}
                    />
                  ))
                ) : (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      lineHeight: 19,
                      color: EMBER.colors.textLight,
                      paddingVertical: 14,
                    }}
                  >
                    {t("access.testing.empty_in_guide")}
                  </Text>
                )}
              </View>
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/* ─── Component ───────────────────────────────────────────────────── */

type Props = NativeStackScreenProps<AccessStackParamList, "Testing">;

export default function TestingScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestingScreen");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const [search, setSearch] = useState("");
  const [selectedGuide, setSelectedGuide] = useState<GuideCard | null>(null);

  const { data: patient } = usePatient();
  const { data: fertilityIntent } = useFertilityIntent();
  const { data: cycles } = useCycles();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const canonicalExperience =
    authorizedExperience.status === "ready"
      ? authorizedExperience.canonical
      : null;
  const isProfileComplete = useMemo(
    () => calculateBasicInfoCompletion(patient).percent >= 0.999,
    [patient],
  );
  const recommendations = useMemo(
    () =>
      getTestingRecommendations(
        patient,
        fertilityIntent,
        cycles,
        5,
        canonicalExperience,
      ),
    [patient, fertilityIntent, cycles, canonicalExperience],
  );
  const visibleSections = useMemo(() => {
    const sections = prioritizeSectionsForGender(
      canonicalExperience?.viewer.sexAtBirth ?? "",
    );
    if (route.params?.focus !== "semen-analysis") return sections;

    const semenIndex = sections.findIndex(
      (section) => section.key === "semen-analysis",
    );
    if (semenIndex < 0) return sections;

    return [
      sections[semenIndex]!,
      ...sections.filter((_, index) => index !== semenIndex),
    ];
  }, [canonicalExperience?.viewer.sexAtBirth, route.params?.focus]);
  const visibleGuideCards = useMemo(() => {
    const visibleSectionKeys = new Set(
      visibleSections.map((section) => section.key),
    );
    return GUIDE_CARDS.filter((card) =>
      card.sections.some((section) => visibleSectionKeys.has(section)),
    );
  }, [visibleSections]);

  const tests = useMemo(
    () =>
      getBlocksByKind("test").filter(
        (test) => !test.body.isCommercialProductProfile,
      ),
    [],
  );
  const products = useMemo(() => getBlocksByKind("product"), []);

  const filteredTests = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tests;
    return tests.filter((test) => testMatchesSearch(test, q));
  }, [tests, search]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((product) => productMatchesSearch(product, q));
  }, [products, search]);

  // Group tests and products by section
  const testsBySection = useMemo(() => {
    const map: Record<string, ContentBlock<"test">[]> = {};
    for (const t of filteredTests) {
      const sec = t.body.section;
      if (sec) {
        (map[sec] ??= []).push(t);
      }
    }
    return map;
  }, [filteredTests]);

  const productsBySection = useMemo(() => {
    const map: Record<string, ContentBlock<"product">[]> = {};
    for (const p of filteredProducts) {
      const sec = p.body.section;
      if (sec) {
        (map[sec] ??= []).push(p);
      }
    }
    return map;
  }, [filteredProducts]);

  const sectionResults = useMemo(
    () =>
      visibleSections
        .map((section) => ({
          section,
          sectionTests: testsBySection[section.key] ?? [],
          sectionProducts: productsBySection[section.key] ?? [],
        }))
        .filter(
          ({ sectionTests, sectionProducts }) =>
            sectionTests.length > 0 || sectionProducts.length > 0,
        ),
    [visibleSections, testsBySection, productsBySection],
  );

  const selectedGuideItems = useMemo(() => {
    if (!selectedGuide) return [];
    return getGuideRelatedItems(
      selectedGuide,
      tests,
      products,
      visibleSections,
    );
  }, [selectedGuide, tests, products, visibleSections]);

  const recommendedItems = useMemo<RecommendedItem[]>(() => {
    if (!isProfileComplete) return [];
    return recommendations
      .map((rec): RecommendedItem | null => {
        const test = tests.find((tt) => String(tt.id) === rec.testId);
        if (!test) return null;
        return {
          testId: rec.testId,
          title: test.title,
          reason: t(rec.reasonKey),
        };
      })
      .filter((x): x is RecommendedItem => x !== null);
  }, [isProfileComplete, recommendations, tests, t]);

  const onItemPress = useCallback(
    (item: ContentBlock<"test"> | ContentBlock<"product">) => {
      const target = getTestRouteTarget(item.id as string);
      if (target.screen === "TestProductDetail") {
        navigation.navigate("TestProductDetail", target.params);
        return;
      }
      navigation.navigate("TestDetail", target.params);
    },
    [navigation],
  );

  const onGuideItemPress = useCallback(
    (item: TestingItem) => {
      setSelectedGuide(null);
      const target = getTestRouteTarget(item.id);
      if (target.screen === "TestProductDetail") {
        navigation.navigate("TestProductDetail", target.params);
        return;
      }
      navigation.navigate("TestDetail", target.params);
    },
    [navigation],
  );

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab
                  ? t("you.shared.back")
                  : t("access.back"),
              ),
              "back",
            );
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {route.params?.returnTab ? t("you.shared.back") : t("access.back")}
          </Text>
        </Pressable>

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("access.testing.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
          numberOfLines={2}
        >
          {t("access.testing.subtitle")}
        </Text>

        <MoonSearchInput
          placeholder={t("access.testing.search_placeholder")}
          value={search}
          onChangeText={setSearch}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {isProfileComplete && recommendedItems.length > 0 ? (
          <TestingPlanHeroCard
            items={recommendedItems}
            onItemPress={(testId) => {
              const target = getTestRouteTarget(testId);
              if (target.screen === "TestProductDetail") {
                navigation.navigate("TestProductDetail", target.params);
              } else {
                navigation.navigate("TestDetail", target.params);
              }
            }}
          />
        ) : !isProfileComplete ? (
          <TestingPlanHeroCard />
        ) : null}

        {/* Guide Cards — "What do you want to know?" */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: EMBER.colors.textFaint,
            letterSpacing: 1,
            textTransform: "uppercase",
            marginTop: 20,
            marginBottom: 12,
          }}
        >
          {t("access.testing.guide_section_label")}
        </Text>
        <View
          style={
            {
              marginHorizontal: -16,
              ...(Platform.OS === "web" && {
                WebkitMaskImage:
                  "linear-gradient(to right, black 90%, transparent 100%)",
                maskImage:
                  "linear-gradient(to right, black 90%, transparent 100%)",
              }),
            } as Record<string, unknown>
          }
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              gap: 12,
              paddingLeft: 16,
              paddingRight: 32,
            }}
          >
            {visibleGuideCards.map((card) => {
              return (
                <Pressable
                  key={card.key}
                  onPress={() => {
                    capturePress(pressLabel(t(card.labelKey)), card.labelKey);
                    setSelectedGuide(card);
                  }}
                  style={{
                    width: 170,
                    height: 160,
                    borderRadius: 20,
                    backgroundColor: "rgba(255,255,255,0.45)",
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.6)",
                    padding: 18,
                    justifyContent: "space-between",
                  }}
                >
                  <TestingGuideIllustration kind={card.illustration} />
                  <View style={{ gap: 4 }}>
                    <Text
                      className="font-dm-sans"
                      numberOfLines={2}
                      style={{
                        fontSize: 15,
                        fontWeight: "700",
                        color: EMBER.colors.text,
                        lineHeight: 20,
                      }}
                    >
                      {t(card.labelKey)}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      numberOfLines={2}
                      style={{
                        fontSize: 12,
                        color: EMBER.colors.textLight,
                        lineHeight: 17,
                      }}
                    >
                      {t(card.subtitleKey)}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>

        {/* Sections */}
        <View style={{ gap: 22, marginTop: 24 }}>
          {sectionResults.map(({ section, sectionTests, sectionProducts }) => {
            const Icon = section.icon;
            return (
              <View key={section.key} style={{ gap: 8 }}>
                <View style={{ gap: 3, paddingHorizontal: 2 }}>
                  <SectionTitle icon={Icon}>{t(section.titleKey)}</SectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                    }}
                  >
                    {t(section.descriptionKey)}
                  </Text>
                </View>

                <View
                  style={{
                    ...(GLASS_CARD_STYLE as object),
                    paddingHorizontal: 18,
                    paddingVertical: 6,
                  }}
                >
                  {/* Test rows */}
                  {sectionTests.map((test, idx) => {
                    const body = test.body;
                    const locationKey = getTestLocationKey(body);
                    const location = t(
                      `access.testing.location.${locationKey}`,
                    );
                    const locationTone =
                      locationKey === "at_home"
                        ? "green"
                        : locationKey === "clinic"
                          ? "orange"
                          : "neutral";
                    const isLast =
                      idx === sectionTests.length - 1 &&
                      sectionProducts.length === 0;

                    return (
                      <Pressable
                        key={test.id}
                        onPress={() => {
                          capturePress(pressLabel(test.title), "test_row");
                          onItemPress(test);
                        }}
                        style={{
                          paddingVertical: 12,
                          gap: 6,
                          borderBottomWidth: isLast ? 0 : 1,
                          borderBottomColor: "rgba(0,0,0,0.06)",
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 6,
                              flexShrink: 1,
                              minWidth: 0,
                            }}
                          >
                            <StatusBadge
                              label={location}
                              tone={locationTone}
                              icon={MapPin}
                            />
                            {body.turnaroundTime && (
                              <View
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                <Clock
                                  size={9}
                                  color={EMBER.colors.textFaint}
                                  strokeWidth={1.5}
                                />
                                <Text
                                  className="font-dm-sans"
                                  style={{
                                    fontSize: 10,
                                    color: EMBER.colors.textFaint,
                                  }}
                                >
                                  {body.turnaroundTime}
                                </Text>
                              </View>
                            )}
                          </View>
                          {body.costRange && (
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 3,
                                flexShrink: 0,
                              }}
                            >
                              <DollarSign
                                size={11}
                                color={colors.greyBrown}
                                strokeWidth={2}
                              />
                              <Text
                                className="font-dm-sans"
                                numberOfLines={1}
                                style={{
                                  fontSize: 12,
                                  fontWeight: "600",
                                  color: colors.greyBrown,
                                }}
                              >
                                {formatCostShort(body.costRange.low)}–
                                {formatCostShort(body.costRange.high)}
                              </Text>
                            </View>
                          )}
                        </View>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 15,
                            fontWeight: "700",
                            color: EMBER.colors.text,
                          }}
                        >
                          {test.title}
                        </Text>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            color: EMBER.colors.textLight,
                            lineHeight: 18,
                          }}
                          numberOfLines={2}
                        >
                          {body.whatItMeasures}
                        </Text>
                      </Pressable>
                    );
                  })}

                  {/* Products for this section */}
                  {sectionProducts.length > 0 && (
                    <>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: "700",
                          color: EMBER.colors.textMid,
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                          marginTop: 8,
                          marginBottom: 4,
                        }}
                      >
                        {t("access.testing.shop_at_home")}
                      </Text>
                      <View style={{ marginHorizontal: -18 }}>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={{
                            gap: 10,
                            paddingHorizontal: 18,
                            paddingBottom: 4,
                          }}
                        >
                          {sectionProducts.map((product) => {
                            const body = product.body;
                            return (
                              <Pressable
                                key={product.id}
                                onPress={() => {
                                  capturePress(
                                    pressLabel(product.title),
                                    "test_product_card",
                                  );
                                  onItemPress(product);
                                }}
                                style={{
                                  width: 190,
                                  minHeight: 154,
                                  padding: 12,
                                  borderRadius: 16,
                                  backgroundColor: "rgba(255,255,255,0.42)",
                                  borderWidth: 1,
                                  borderColor: "rgba(255,255,255,0.56)",
                                  justifyContent: "space-between",
                                  gap: 10,
                                }}
                              >
                                <View style={{ gap: 10 }}>
                                  {body.imageUrl ? (
                                    <Image
                                      source={{ uri: body.imageUrl }}
                                      style={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 10,
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      }}
                                    />
                                  ) : product.icon ? (
                                    <Text style={{ fontSize: 26 }}>
                                      {product.icon}
                                    </Text>
                                  ) : null}
                                  <View style={{ gap: 3 }}>
                                    <Text
                                      className="font-dm-sans"
                                      style={{
                                        fontSize: 14,
                                        fontWeight: "700",
                                        color: EMBER.colors.text,
                                        lineHeight: 18,
                                      }}
                                      numberOfLines={2}
                                    >
                                      {body.name}
                                    </Text>
                                    {body.chips?.length ? (
                                      <Text
                                        className="font-dm-sans"
                                        style={{
                                          fontSize: 11,
                                          color: EMBER.colors.textFaint,
                                          lineHeight: 15,
                                        }}
                                        numberOfLines={2}
                                      >
                                        {body.chips.join(" · ")}
                                      </Text>
                                    ) : null}
                                  </View>
                                </View>
                                <View
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "baseline",
                                    gap: 6,
                                  }}
                                >
                                  <Text
                                    className="font-dm-sans"
                                    style={{
                                      fontSize: 16,
                                      fontWeight: "700",
                                      color: colors.greyBrown,
                                    }}
                                  >
                                    ${body.price}
                                  </Text>
                                  {body.originalPrice ? (
                                    <Text
                                      className="font-dm-sans"
                                      style={{
                                        fontSize: 11,
                                        color: EMBER.colors.textFaint,
                                        textDecorationLine: "line-through",
                                      }}
                                    >
                                      ${body.originalPrice}
                                    </Text>
                                  ) : null}
                                </View>
                              </Pressable>
                            );
                          })}
                        </ScrollView>
                      </View>
                    </>
                  )}
                </View>
              </View>
            );
          })}
          {sectionResults.length === 0 ? (
            <View style={{ alignItems: "center", paddingTop: 20 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  color: EMBER.colors.textLight,
                  textAlign: "center",
                }}
              >
                {search.trim()
                  ? t("access.testing.empty_search")
                  : t("access.testing.empty_no_options")}
              </Text>
            </View>
          ) : null}
        </View>
      </ScrollView>

      <TestingGuideSheet
        card={selectedGuide}
        items={selectedGuideItems}
        onClose={() => setSelectedGuide(null)}
        onItemPress={onGuideItemPress}
      />
    </GlassScreenLayout>
  );
}
